import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { cameraService } from "client-app-core";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getTranslation } from "orion-components/i18n";
import { toast } from "react-toastify"; // cSpell:ignore toastify

const AddBookmarkDialog = ({ open, cameraId, timestamp, onClose, container }) => {
	const [description, setDescription] = useState("");
	const [descriptionEdited, setDescriptionEdited] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const dialogContainer = useMemo(() => {
		const fullscreenElement = document.fullscreenElement;
		const usePlayerContainer =
			container && fullscreenElement && (fullscreenElement === container || fullscreenElement.contains(container));

		return usePlayerContainer ? () => container : undefined;
	}, [container, open]);

	useEffect(() => {
		if (open && timestamp && !descriptionEdited) {
			setDescription(timestamp ? timestamp.toLocaleString() : "");
		}
	}, [descriptionEdited, open, timestamp]);

	const handleClose = () => {
		if (isSaving) {
			return;
		}

		setDescription("");
		setDescriptionEdited(false);
		onClose();
	};

	const handleSave = () => {
		const trimmedDescription = description.trim();
		if (!cameraId || !timestamp || !trimmedDescription) {
			return;
		}

		setIsSaving(true);

		cameraService.createBookmark(
			cameraId,
			{
				timestamp: timestamp.toISOString(),
				description: trimmedDescription
			},
			err => {
				setIsSaving(false);

				if (err) {
					toast.clearWaitingQueue();
					toast(err.message || "Failed to save bookmark.", { type: "error" });
					return;
				}

				EventBus.publish("CameraBookmarks_updated", { cameraId });
				handleClose();
			}
		);
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			container={dialogContainer}
			sx={{ zIndex: 999999 }} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-dialog"
		>
			<DialogTitle data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-dialog-title">
				<Typography sx={{ fontWeight: 400, fontSize: "22px" }}>
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveBookmarkDialog.title")}
				</Typography>
			</DialogTitle>
			<DialogContent data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-dialog-content">
				<Typography sx={{ mb: 2 }}>
					{getTranslation(
						"global.CBComponents.newVideoPlayer.components.mainMenu.saveBookmarkDialog.description"
					)}
				</Typography>
				<TextField
					autoFocus={true}
					fullWidth={true}
					label={getTranslation(
						"global.CBComponents.newVideoPlayer.components.mainMenu.saveBookmarkDialog.bookmarkName"
					)}
					value={description}
					onChange={event => {
						setDescriptionEdited(true);
						setDescription(event.target.value);
					}}
					variant="standard"
					required={true}
					error={!description.trim()} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-text-field"
				/>
			</DialogContent>
			<DialogActions data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-dialog-actions">
				<Button variant="text" onClick={handleClose} disabled={isSaving} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-button">
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveBookmarkDialog.cancel")}
				</Button>
				<Button variant="text" onClick={handleSave} disabled={isSaving || !timestamp || !description.trim()} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-add-bookmark-dialog-button-2">
					{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.saveBookmarkDialog.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

AddBookmarkDialog.propTypes = {
	open: PropTypes.bool,
	cameraId: PropTypes.string,
	timestamp: PropTypes.instanceOf(Date),
	onClose: PropTypes.func,
	container: PropTypes.any
};

AddBookmarkDialog.defaultProps = {
	open: false,
	cameraId: null,
	timestamp: null,
	onClose: () => {},
	container: null
};

export default AddBookmarkDialog;
